/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// utils
import CanvasUtils from "utils/canvas-utils/CanvasUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

class ConversionUtils {

  /**
  * @param {String} colorHex
  * @returns {Array<Number>}
  */
  static colorHexToRgb(colorHex) {
    if(!ValidationUtils.colorHex(colorHex)) {
      throw Error(`Cannot convert colorHexToRgb, value is not a colorHex: ${colorHex}.`);
    }

    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    colorHex = colorHex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorHex);
    return result
      ? [
          parseInt(result[1], 16), //NOSONAR
          parseInt(result[2], 16), //NOSONAR
          parseInt(result[3], 16), //NOSONAR
        ]
      : null;
  }

  /**
  * @param {Number} r
  * @param {Number} g
  * @param {Number} b
  * @returns {String}
  */
  static colorRgbToHex(r, g, b) {
    if(!ValidationUtils.colorRgb(r)) {
      throw Error(`Cannot convert colorRgbToHex, R value is not a RGB: ${r}.`);
    }
    if(!ValidationUtils.colorRgb(g)) {
      throw Error(`Cannot convert colorRgbToHex, G value is not a RGB: ${g}.`);
    }
    if(!ValidationUtils.colorRgb(b)) {
      throw Error(`Cannot convert colorRgbToHex, B value is not a RGB: ${b}.`);
    }
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`; //NOSONAR
  }

  /**
  * @param {String} html
  * @returns {String}
  */
  static htmlToPlainText(html) {
    return String(html).replace(/<[^>]*>/g, "");
  }

  /**
  * @param {HTMLImageElement} image
  * @returns {String}
  */
  static imageToBase64(image) {
    return new Promise ((resolve) => {
      const canvas = CanvasUtils.createUnscaled(image.naturalWidth, image.naturalHeight);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image,
        0, 0, image.naturalWidth, image.naturalHeight,
        0, 0, image.naturalWidth, image.naturalHeight
      );
      resolve(canvas.toDataURL("image/png"));
      CanvasUtils.clean(canvas);
    });
  }

  /**
  * @param {HTMLImageElement} image
  * @returns {Blob}
  */
  static imageToBlob(image) {
    return new Promise ((resolve) => {
      const canvas = CanvasUtils.createUnscaled(image.naturalWidth, image.naturalHeight);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image,
        0, 0, image.naturalWidth, image.naturalHeight,
        0, 0, image.naturalWidth, image.naturalHeight
      );
      canvas.toBlob((blob) => {
        resolve(blob);
        CanvasUtils.clean(canvas);
      });
    });
  }

  /**
  * @param {String} stringLatLng
  * @returns {Object}
  */
  static stringToLatLng(stringLatLng) {
    if(!ValidationUtils.string(stringLatLng)) {
      throw Error(`Cannot convert stringToLatLng, value is not a string: ${stringLatLng}.`);
    }

    const destructuredLatLng = stringLatLng
      .split(" ").join("")
      .split("(").join("")
      .split(")").join("")
      .split("{").join("")
      .split("}").join("")
      .split("\"").join("")
      .split(":").join("")
      .split("lat").join("")
      .split("lng").join("")
      .split(",");
    const [lat, lng] = destructuredLatLng;

    if(!ValidationUtils.float(lat)) {
      throw Error(`Cannot convert stringToLatLng, lat is not a float: ${lat}.`);
    }
    if(!ValidationUtils.float(lng)) {
      throw Error(`Cannot convert stringToLatLng, lng is not a float: ${lng}.`);
    }
    return { lat: Number(lat), lng: Number(lng) };
  }

  /**
  * @param {String} stringPov
  * @returns {Object}
  */
  static stringToPov(stringPov) {
    if(!ValidationUtils.string(stringPov)) {
      throw Error(`Cannot convert stringToPov, value is not a string: ${stringPov}.`);
    }

    const destructuredPov = stringPov
      .split(" ").join("")
      .split("(").join("")
      .split(")").join("")
      .split("{").join("")
      .split("}").join("")
      .split("\"").join("")
      .split(":").join("")
      .split("heading").join("")
      .split("pitch").join("")
      .split("zoom").join("")
      .split(",");
    const [ heading, pitch, zoom ] = destructuredPov;

    if(!ValidationUtils.float(heading)) {
      throw Error(`Cannot convert stringToPov, heading is not a float: ${heading}.`);
    }
    else if(!ValidationUtils.float(pitch)) {
      throw Error(`Cannot convert stringToPov, pitch is not a float: ${pitch}.`);
    }
    else if(!ValidationUtils.float(zoom)) {
      throw Error(`Cannot convert stringToPov, zoom is not a float: ${zoom}.`);
    }
    return {
      heading: Number(heading),
      pitch: Number(pitch),
      zoom: Number(zoom)
    };
  }

}
export default ConversionUtils;
